
import { Component, Vue, Watch, Mixins } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action } from "vuex-class";

import DispatchDetails from "@/components/mobile/dispatch/DispatchDetails.vue";
import CallSignList from "@/components/mobile/dispatch/CallSignList.vue";
import CloseProximity from "@/components/mobile/dispatch/CloseProximity.vue";
import QuickCloseList from "@/components/mobile/dispatch/QuickCloseList.vue";

import { DispatchRequest, MobileFeatures, DispatchType, GuardStatus, Mobile as MobileState, TrackingToggle } from "@/store/mobile/types";
import MobileMixin from "@/mixins/MobileMixin";

const Mobile = namespace("mobile");
const SessionStore = namespace("sessions");

@Component({
	components: {
		"call-sign-list": CallSignList,
		"dispatch-details": DispatchDetails,
		"close-proximity": CloseProximity,
		"quick-close-list": QuickCloseList,
	}
})
export default class DispatchCore extends Mixins(MobileMixin) {
	// Feature toggle
	@Getter getFeature: (featureName: string[]) => boolean;
	@Mobile.Getter("getMobileFeatures") getMobileFeatures: MobileFeatures;
	@Mobile.Mutation setMapEnabled: any;
	@Mobile.Mutation setNotificationText: any;

	// Call signs
	@Mobile.State((state: MobileState) => state.DispatchGuard.callSign) callSign: string;
	@Mobile.Getter("getGuardId") guardId: number;
	@Mobile.Action loadGuardForUser: () => Promise<void>;

	// Quick close
	@Mobile.Action loadQuickCloseOutcomes: () => Promise<void>;

	// Availability
	@Mobile.Getter("getOnDuty") onDuty: boolean;
	@Mobile.State((state: MobileState) => state.DispatchGuard.statusEnum) statusEnum: GuardStatus;
	@Mobile.State((state: MobileState) => state.CoverRequested) coverRequested: boolean;
	@Mobile.Mutation setOnDuty: any;
	@Mobile.State((state: MobileState) => state.DispatchGuard.hasArrived) hasArrived: boolean;

	// Device
	@Mobile.Action registerUserDevice: any;

	// Maps
	@Mobile.Getter("getFocalPoint") focalPoint: any;

	// Dispatch Requests
	@Mobile.Getter("getDispatchRequests") dispatchRequests: DispatchRequest[];
	@Mobile.Action loadDispatchRequestsForGuard: any;

	@Mobile.State((state: MobileState) => state.DispatchGuard.almostArrived) almostArrived: boolean;

	// Other
	@Mobile.Mutation toggleRightSidebarVisibility: () => void;

	private dispatchComponent = "";

	public get isMobileDispatchEnabled() {
		return this.getFeature(["Mobile", "Dispatch"]);
	}

	public get isMobileLocationTrackingEnabled() {
		return this.getFeature(["Mobile", "Dispatch", "LocationTracking"]);
	}

	mounted() {
		// Get the guard for current user (will restore guard from current session)
		this.loadGuardForUser();

		// Load quick close outcomes for user.
		this.loadQuickCloseOutcomes();
		this.dispatchComponent = "call-sign-list";
		this.setMapEnabled(true);
	}

	beforeDestroy() {
		// Ensure that the polling for new dispatches stops
		this.stopGettingNextTopDispatch();
		this.stopBackgroundLocationTracking();
	}

	@Watch("almostArrived")
	public onAlmostArrived(value: boolean, oldValue: boolean) {
		if (value) {
			this.dispatchComponent = "close-proximity";
		} else {
			if (this.dispatchComponent === "close-proximity") {
				this.dispatchComponent = "";
			}
		}
	}

	@Watch("dispatchRequests")
	onDispatchRequestsChanged(value: DispatchRequest[], oldValue: DispatchRequest[]) {
		// When we get new dispatches, raise a notification
		if (oldValue.length < value.length) {
			const dispatchRequest = value[value.length - 1];
			const notificationText = dispatchRequest.dispatchType == DispatchType.Tour ?
				`New tour: ${dispatchRequest.areaTitle}` :
				`New dispatch: ${dispatchRequest.areaTitle} - ${(dispatchRequest.serverTypeEventTitle ? dispatchRequest.serverTypeEventTitle : "")}`;
			this.setNotificationText(notificationText);
		}
	}

	@Watch("callSign")
	async onCallSignChanged(value: string, oldValue: string) {
		if (value) {
			this.dispatchComponent = "";
			this.setOnDuty(true);

			if (this.isLoadedInApp()) {
				await this.registerUserDevice({
					deviceId: window.notificationSettings.Id,
					deviceType: window.notificationSettings.DeviceType,
				});
				this.startBackgroundLocationTracking();
			}
		} else {
			this.dispatchComponent = "call-sign-list";
		}
	}

	private startBackgroundLocationTracking() {
		if (!this.isMobileLocationTrackingEnabled) {
			return;
		}

		if (this.isLoadedInApp()) {
			const trackingPayload: TrackingToggle = {
					toggle: "on",
				};
			this.sendTrackingRequestToDevice(trackingPayload);
		}
	}

	private stopBackgroundLocationTracking() {
		if (this.isLoadedInApp()) {
			const trackingPayload: TrackingToggle = {
					toggle: "off",
				};
			this.sendTrackingRequestToDevice(trackingPayload);
		}
	}

	private sendTrackingRequestToDevice(trackingPayload: TrackingToggle) {
		if (this.isiOS()) {
			(window as any).webkit.messageHandlers.trackingRequestHandler.postMessage(trackingPayload);
		} else if (this.isAndroid()) {
			const trackingPayloadSerialized = JSON.stringify(trackingPayload);
			window.android.trackingRequest(trackingPayloadSerialized);
		}
	}

	@Watch("onDuty")
	onOnDutyChanged(value: boolean, oldValue: boolean) {
		if (value) {
			// Get the list straight away (so it loads quickly)
			this.loadDispatchRequestsForGuard(this.guardId);
			if (this.newDispatchRequestInterval === null) {
				// Then every 5 seconds, poll for an updated queue
				this.getNextTopDispatch();
			}
		} else {
			this.stopGettingNextTopDispatch();
		}
	}

	@Watch("hasArrived")
	onHasArrived(value: boolean, oldValue: boolean) {
		if (value) {
			this.dispatchComponent = "dispatch-details";
		} else {
			this.dispatchComponent = "";
		}
	}

	@Watch("focalPoint")
	onFocalPointChanged(value: google.maps.LatLng, oldValue: google.maps.LatLng) {
		this.toggleRightSidebarVisibility();
	}

	finishedDispatch(dispatchRequestId: number) {
		this.dispatchComponent = "";
	}

	/* --- DISPATCH QUEUE POLLING FUNCTIONS --- */
	newDispatchRequestInterval = null;
	getNextTopDispatch() {
		clearTimeout(this.newDispatchRequestInterval);
		this.newDispatchRequestInterval = setInterval(
			async function() {
				this.loadDispatchRequestsForGuard(this.guardId);

				// Try reloading the guard's Id if they have requested to cover for another guard
				if (this.statusEnum == GuardStatus.CoveringRequested) {
					await this.loadGuardForUser();
				}
			}.bind(this),
			5000
		);
	}

	stopGettingNextTopDispatch() {
		clearTimeout(this.newDispatchRequestInterval);
		this.newDispatchRequestInterval = null;
	}

	// Method to show quick close view.
	quickClose() {
		this.dispatchComponent = "quick-close-list";
	}

	quickCloseFinishDispatch(finished: boolean) {
		// True case, hide component, outcome selected.
		if (finished) {
			this.dispatchComponent = "";
			return;
		}
		// False case, outcome not selected, back to dispatch details.
		this.dispatchComponent = "dispatch-details";
	}
}